/**
 * @module AuthTelemetry
 * @summary Lightweight telemetry helper for Firebase authentication flows.
 *
 * @description
 * Tracks auth attempt lifecycle metadata (timestamps, durations, retry counts,
 * Firebase error codes, and network health) without persisting PII. Consumers
 * can pipe the sanitized payloads into pluggable sinks such as the console,
 * Sentry, or Google Analytics.
 */

const CODE_TOKEN_PATTERN = /^[A-Za-z0-9_.:/-]+$/;

/**
 * Generates monotonically increasing attempt identifiers.
 *
 * @returns {() => string} generator returning the next attempt id as a string.
 */
function createAttemptIdGenerator() {
  let index = 0;

  return () => {
    index += 1;
    return `auth_attempt_${index}`;
  };
}

/**
 * Returns a default clock implementation that falls back to `Date.now()`.
 *
 * @returns {() => number} function returning the current time in milliseconds.
 */
function createDefaultClock() {
  return () => Date.now();
}

/**
 * Evaluates whether a value is a safe code token (no spaces or PII markers).
 *
 * @param {unknown} value potential token value.
 * @returns {string|number|boolean|null|undefined} sanitized token or `undefined`
 *          when the input looks unsafe.
 */
function sanitizeToken(value) {
  if (value === null) {
    return null;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  if (!trimmed || trimmed.length > 64) {
    return undefined;
  }

  if (!CODE_TOKEN_PATTERN.test(trimmed)) {
    return undefined;
  }

  return trimmed;
}

/**
 * Extracts retry count while coercing it into an integer.
 *
 * @param {unknown} value incoming retry count.
 * @returns {number} sanitized retry count (>= 0).
 */
function sanitizeRetryCount(value) {
  const parsed = Number.parseInt(String(value ?? '0'), 10);

  if (Number.isNaN(parsed) || parsed < 0) {
    return 0;
  }

  return parsed;
}

/**
 * Sanitizes the contextual metadata attached to an auth attempt.
 *
 * @param {Record<string, unknown>} context context values supplied by callers.
 * @returns {Record<string, unknown>} sanitized shallow copy of the context.
 */
function sanitizeContext(context = {}) {
  const sanitized = {};

  Object.entries(context).forEach(([key, value]) => {
    if (key === 'retryCount') {
      sanitized.retryCount = sanitizeRetryCount(value);
      return;
    }

    const safeValue = sanitizeToken(value);

    if (typeof safeValue !== 'undefined') {
      sanitized[key] = safeValue;
    }
  });

  if (typeof sanitized.retryCount === 'undefined') {
    sanitized.retryCount = 0;
  }

  return sanitized;
}

/**
 * Creates a sanitized view of a Firebase auth error.
 *
 * @param {unknown} error Firebase error object.
 * @returns {{ code?: string, name?: string }|null} sanitized error payload.
 */
function sanitizeError(error) {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const potential = /** @type {{ code?: unknown, name?: unknown }} */ (error);
  const safeCode = sanitizeToken(potential.code);
  const safeName = sanitizeToken(potential.name);

  if (!safeCode && !safeName) {
    return null;
  }

  const sanitized = {};

  if (safeCode) {
    sanitized.code = safeCode;
  }

  if (safeName) {
    sanitized.name = safeName;
  }

  return sanitized;
}

/**
 * Normalises raw NetHealth input (string or object) into a safe status token.
 *
 * @param {unknown} netHealth raw NetHealth status or container.
 * @returns {string|null} sanitized net health code.
 */
function sanitizeNetHealth(netHealth) {
  if (!netHealth) {
    return null;
  }

  if (typeof netHealth === 'string') {
    const safeToken = sanitizeToken(netHealth.toUpperCase());
    return typeof safeToken === 'string' ? safeToken : null;
  }

  if (typeof netHealth === 'object') {
    const container = /** @type {{ status?: unknown }} */ (netHealth);

    if (typeof container.status !== 'undefined') {
      return sanitizeNetHealth(container.status);
    }
  }

  return null;
}

/**
 * Emits telemetry events into configured sinks while shielding PII.
 */
export class AuthTelemetry {
  /**
   * @param {Object} [options] configuration overrides.
   * @param {Array<(event: AuthTelemetryEvent) => void>} [options.sinks]
   *        sink callbacks that receive telemetry events.
   * @param {() => number} [options.clock] monotonic clock used for durations.
   * @param {() => unknown} [options.netHealthProvider]
   *        callback returning the latest NetHealth status.
   */
  constructor({ sinks = [consoleSink], clock, netHealthProvider } = {}) {
    /** @private */
    this._clock = clock ?? createDefaultClock();
    /** @private */
    this._netHealthProvider = netHealthProvider ?? null;
    /** @private */
    this._sinks =
      Array.isArray(sinks) && sinks.length ? sinks.slice() : [consoleSink];
    /** @private */
    this._createAttemptId = createAttemptIdGenerator();
    /** @private */
    this._activeAttempt = null;
  }

  /**
   * Registers an additional sink at runtime.
   *
   * @param {(event: AuthTelemetryEvent) => void} sink sink callback function.
   * @returns {void}
   */
  addSink(sink) {
    if (typeof sink === 'function') {
      this._sinks.push(sink);
    }
  }

  /**
   * Begins tracking a new auth attempt.
   *
   * @param {Record<string, unknown>} [context] contextual metadata for the attempt.
   * @returns {string} attempt identifier for downstream correlation.
   */
  startAttempt(context = {}) {
    const timestamp = this._clock();
    const sanitizedContext = sanitizeContext(context);
    const attemptId = this._createAttemptId();
    const netHealthStatus = this._readNetHealth();

    const attempt = {
      id: attemptId,
      context: sanitizedContext,
      startedAt: timestamp,
      endedAt: null,
      durationMs: null,
      retryCount: sanitizedContext.retryCount ?? 0,
      result: null,
      error: null,
      steps: [
        {
          type: 'start',
          timestamp,
          netHealth: netHealthStatus,
        },
      ],
    };

    this._activeAttempt = attempt;
    this._emit('start', attempt);

    return attemptId;
  }

  /**
   * Finalises the active attempt and emits an `end` event.
   *
   * @param {Object} [options] terminal data for the attempt.
   * @param {unknown} [options.result] completion result token.
   * @param {unknown} [options.error] Firebase error instance (optional).
   * @returns {AuthTelemetryAttempt|null} public snapshot of the finished attempt.
   */
  endAttempt({ result, error } = {}) {
    if (!this._activeAttempt) {
      return null;
    }

    const timestamp = this._clock();
    const attempt = this._activeAttempt;
    const safeResult = sanitizeToken(result);
    const safeError = sanitizeError(error);
    const duration = Math.max(0, timestamp - attempt.startedAt);
    const netHealthStatus = this._readNetHealth();

    attempt.endedAt = timestamp;
    attempt.durationMs = duration;
    attempt.result = typeof safeResult !== 'undefined' ? safeResult : null;
    attempt.error = safeError;
    attempt.steps.push({
      type: 'end',
      timestamp,
      netHealth: netHealthStatus,
    });

    const snapshot = this._snapshot(attempt);
    this._emit('end', snapshot);
    this._activeAttempt = null;

    return snapshot;
  }

  /**
   * Clears the active attempt without emitting telemetry.
   *
   * @returns {void}
   */
  reset() {
    this._activeAttempt = null;
  }

  /**
   * Provides a read-only snapshot with derived fields.
   *
   * @private
   * @param {AuthTelemetryAttempt} attempt mutable attempt record.
   * @returns {AuthTelemetryAttempt} frozen snapshot suitable for sinks.
   */
  _snapshot(attempt) {
    const startStep = attempt.steps[0] ?? null;
    const endStep = attempt.steps[attempt.steps.length - 1] ?? null;
    const snapshot = {
      id: attempt.id,
      context: attempt.context,
      startedAt: attempt.startedAt,
      endedAt: attempt.endedAt,
      durationMs: attempt.durationMs,
      retryCount: attempt.retryCount,
      result: attempt.result,
      error: attempt.error,
      netHealth: {
        start: startStep ? startStep.netHealth : null,
        end: endStep ? endStep.netHealth : null,
      },
      steps: attempt.steps.map((step) => ({
        type: step.type,
        timestamp: step.timestamp,
        netHealth: step.netHealth,
      })),
    };

    return Object.freeze(snapshot);
  }

  /**
   * Reads the latest NetHealth status through the provided provider.
   *
   * @private
   * @returns {string|null} sanitized net health code.
   */
  _readNetHealth() {
    if (!this._netHealthProvider) {
      return null;
    }

    try {
      const rawStatus = this._netHealthProvider();
      return sanitizeNetHealth(rawStatus);
    } catch (err) {
      console.warn('AuthTelemetry: failed to read NetHealth status', err);
      return null;
    }
  }

  /**
   * Dispatches a telemetry event to every registered sink.
   *
   * @private
   * @param {"start"|"end"} type event type identifier.
   * @param {AuthTelemetryAttempt} attempt attempt payload for the sinks.
   * @returns {void}
   */
  _emit(type, attempt) {
    const payload = type === 'start' ? this._snapshot(attempt) : attempt;

    this._sinks.forEach((sink) => {
      try {
        sink({
          event: type,
          attempt: payload,
        });
      } catch (err) {
        console.warn('AuthTelemetry sink failure', err);
      }
    });
  }
}

/**
 * Console sink intended for development.
 *
 * @param {AuthTelemetryEvent} event telemetry event payload.
 * @returns {void}
 */
export function consoleSink(event) {
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug('[AuthTelemetry]', event);
  }
}

/**
 * @typedef {Object} AuthTelemetryAttempt
 * @property {string} id unique attempt identifier.
 * @property {Record<string, unknown>} context sanitized attempt context.
 * @property {number} startedAt start timestamp (ms).
 * @property {number|null} endedAt end timestamp (ms) or null.
 * @property {number|null} durationMs duration in milliseconds or null.
 * @property {number} retryCount number of retries observed.
 * @property {string|null} result sanitized result token or null.
 * @property {{ code?: string, name?: string }|null} error sanitized error payload.
 * @property {{ start: string|null, end: string|null }} netHealth net health summary.
 * @property {Array<{ type: string, timestamp: number, netHealth: string|null }>} steps chronological steps.
 */

/**
 * @typedef {Object} AuthTelemetryEvent
 * @property {"start"|"end"} event type of telemetry event.
 * @property {AuthTelemetryAttempt} attempt attempt payload snapshot.
 */

export default AuthTelemetry;
